/* eslint-disable no-unused-vars */
import axios from 'axios';
import AxiosMockAdapter from 'axios-mock-adapter';
import base64 from 'crypto-js/enc-base64';
import utf8 from 'crypto-js/enc-utf8';
import AES from 'crypto-js/aes';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import enc from 'crypto-js/enc-utf8';
import { getAPIEndpoints, getEnvVars } from 'src/utils/general.utils';
import { v4 as uuidv4 } from 'uuid';

// Global vars
let axiosInstance;
let axiosMock;
let ENV_VARS;
let apiEndpoints;

const getRsaEncryption = msg => {
  return new Promise((resolve, reject) => {
    try {
      (async () => {
        const JSEncrypt = (await import('jsencrypt')).JSEncrypt;
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(getEnvVars().rsaPublicKey);
        let encryptedLoginData = encrypt.encrypt(msg);
        resolve(encryptedLoginData);
      })();
    } catch (error) {
      reject(error);
    }
  });
};

const trimURIParams = uri => {
  if (uri.includes('?')) return uri.split('?')[0];
  return uri;
};

const getHmacEncryption = async (httpVerb, requestURI) => {
  let timeStamp = String(Date.now());
  let nonce = uuidv4();
  let stringToSign = httpVerb + '\n' + timeStamp + '\n' + nonce + '\n' + trimURIParams(requestURI);
  let encodedPublicKey = await getRsaEncryption('DPLNX');
  let hash = hmacSHA256(stringToSign.trim(), decryptKey(getEnvVars().hmacSecretKey));
  let signature = base64.stringify(utf8.parse(hash));
  return `${encodedPublicKey};${nonce};${timeStamp};${signature}`;
};

// Axios default instance
function createAxiosInstance() {
  const Authorization = getAuthorization();
  const userinfo = getUserInfo();
  const baseURL = getBaseUrl();

  const defaultOptions = {
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Authorization,
      userinfo,
    },
  };

  axiosInstance = axios.create(defaultOptions);
  axios.interceptors.request.use(handleRequestInterceptor);
  axios.interceptors.response.use(handleResponseInterceptor);
}

function setEnvVarsForHttpClient(envVars) {
  ENV_VARS = envVars;
}

function setEnvEndpoints(apis) {
  apiEndpoints = apis[ENV_VARS['ENV']];
}

function getAuthorization() {
  let authToken = sessionStorage.getItem('authToken');
  return authToken ? authToken : '';
}

function getUserInfo() {
  let userInfo = sessionStorage.getItem('userInfo');
  return userInfo ? userInfo : '';
}

function getBaseUrl(url) {
  return getAPIEndpoints()?.baseUrl;
}

function handleRequestInterceptor(request) {
  return request;
}

function handleResponseInterceptor(response) {
  return response;
}

// Axios Mock adapter
function getAxiosMockAdapter(options = { delayResponse: 0 }) {
  axiosMock = new AxiosMockAdapter(axiosInstance, options);
}

// Api Methods
async function getData(url, headers, config) {
  const hashAuth = await getHmacEncryption('GET', url);
  return axiosInstance.get(url, {
    ...config,
    headers: {
      ...headers,
      'X-CT-Authorization': hashAuth,
    },
  });
}

async function postData(url, body, headers, config) {
  const hashAuth = await getHmacEncryption('POST', url);
  return axiosInstance.post(url, body, {
    ...config,
    headers: {
      ...headers,
      'X-CT-Authorization': hashAuth,
    },
  });
}

async function putData(url, body, headers) {
  const hashAuth = await getHmacEncryption('PUT', url);
  return axiosInstance.put(url, body, {
    headers: {
      ...headers,
      'X-CT-Authorization': hashAuth,
    },
  });
}

async function deleteData(url, body, headers) {
  const hashAuth = await getHmacEncryption('DELETE', url);
  return axiosInstance.delete(url, {
    data: body,
    headers: {
      ...headers,
      'X-CT-Authorization': hashAuth,
    },
  });
}

function encryptKey(string) {
  return AES.encrypt(string, 'ace-deep-learn-admin').toString();
}

function decryptKey(encryptedText) {
  const bytes = AES.decrypt(encryptedText, 'ace-deep-learn-admin');
  return enc.stringify(bytes);
}

export {
  getData,
  postData,
  putData,
  deleteData,
  createAxiosInstance,
  getAxiosMockAdapter,
  setEnvVarsForHttpClient,
  setEnvEndpoints,
  encryptKey,
  decryptKey,
};
